"use client";

import { useState, useMemo, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type {
  FacetBucket,
  FacetMatchMode,
} from "modules/collections/details/model/collection";
import type { GameId } from "modules/common/model/models";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import { FacetCheckbox } from "./FacetCheckbox";
import { GameDivider, GameGroupWrapper } from "./FacetGameDivider";
import { formatFacetLabel } from "./facetPresentation";

interface FacetGroupProps {
  title: string;
  buckets: FacetBucket[];
  selected: Array<string | number>;
  onToggle: (value: string | number, game?: string) => void;
  defaultOpen?: boolean;
  formatLabel?: (value: string | number) => string;
  renderIcon?: (value: string | number, game?: GameId) => ReactNode;
  matchMode?: FacetMatchMode;
  onMatchModeChange?: (mode: FacetMatchMode) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  gameExclusive?: GameId;
  gameScope?: GameId;
  onClear?: () => void;
  disabledValues?: Set<string | number>;
  matchModeLocked?: boolean;
}

export function FacetGroup({
  title,
  buckets,
  selected,
  onToggle,
  defaultOpen = false,
  formatLabel,
  renderIcon,
  matchMode,
  onMatchModeChange,
  isOpen: controlledIsOpen,
  onToggleOpen,
  gameExclusive,
  gameScope,
  onClear,
  disabledValues,
  matchModeLocked,
}: FacetGroupProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen ?? internalOpen;
  const handleToggle = onToggleOpen ?? (() => setInternalOpen((o) => !o));

  const activeCount = selected.length;
  const showMatchToggle = !!onMatchModeChange && activeCount >= 1;

  const safeBuckets = useMemo(() => {
    const serverBuckets = buckets ?? [];
    if (selected.length === 0) return serverBuckets;

    const existing = new Set(serverBuckets.map((b) => String(b.value)));
    const placeholders: FacetBucket[] = [];
    for (const val of selected) {
      if (!existing.has(String(val))) {
        placeholders.push({ value: val, count: 0 });
      }
    }
    return placeholders.length > 0
      ? [...placeholders, ...serverBuckets]
      : serverBuckets;
  }, [buckets, selected]);
  const gameGroups = useMemo(() => {
    const gamesPresent = new Set(
      safeBuckets.filter((b) => b.game).map((b) => b.game!)
    );
    if (gamesPresent.size <= 1) return null;

    const groups: { game: string; buckets: FacetBucket[] }[] = [];
    const byGame = new Map<string, FacetBucket[]>();
    for (const b of safeBuckets) {
      const g = b.game ?? "";
      if (!byGame.has(g)) byGame.set(g, []);
      byGame.get(g)!.push(b);
    }
    for (const [game, items] of byGame) {
      groups.push({ game, buckets: items });
    }
    return groups;
  }, [safeBuckets]);

  return (
    <div className="border-base-content/10 border-b last:border-b-0">
      <div className="hover:bg-base-300 flex items-center transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm font-medium"
          onClick={handleToggle}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          />
          <span className="flex-1 text-left">{title}</span>
          {gameExclusive && (
            <span className="text-base-content/40 flex items-center">
              <GameIcon game={gameExclusive} />
            </span>
          )}
          {activeCount > 0 && (
            <span className="badge badge-primary badge-xs">{activeCount}</span>
          )}
        </button>
        {activeCount > 0 && onClear && (
          <button
            type="button"
            className="text-base-content/50 hover:text-error pr-2 text-xs transition-colors"
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-0.5 px-1 pb-2">
            {showMatchToggle && (
              <MatchModeToggle
                mode={matchMode ?? "or"}
                onChange={onMatchModeChange}
                disabled={matchModeLocked}
              />
            )}
            {gameGroups
              ? gameGroups.map(({ game, buckets: gameBuckets }) => (
                  <div key={game}>
                    <GameDivider game={game} />
                    <GameGroupWrapper game={game}>
                      {gameBuckets.map((bucket) => (
                        <FacetCheckbox
                          key={`${game}-${String(bucket.value)}`}
                          label={formatFacetLabel(bucket.value, formatLabel)}
                          count={bucket.count}
                          checked={selected.includes(bucket.value)}
                          disabled={
                            (bucket.count === 0 &&
                              !selected.includes(bucket.value)) ||
                            !!disabledValues?.has(bucket.value)
                          }
                          onToggle={onToggle}
                          value={bucket.value}
                          game={game}
                          icon={renderIcon?.(bucket.value, game)}
                        />
                      ))}
                    </GameGroupWrapper>
                  </div>
                ))
              : safeBuckets.map((bucket) => (
                  <FacetCheckbox
                    key={String(bucket.value)}
                    label={formatFacetLabel(bucket.value, formatLabel)}
                    count={bucket.count}
                    checked={selected.includes(bucket.value)}
                    disabled={
                      (bucket.count === 0 &&
                        !selected.includes(bucket.value)) ||
                      !!disabledValues?.has(bucket.value)
                    }
                    onToggle={onToggle}
                    value={bucket.value}
                    icon={renderIcon?.(
                      bucket.value,
                      bucket.game ?? gameScope ?? gameExclusive
                    )}
                  />
                ))}
            {!safeBuckets.length && (
              <span className="text-base-content/40 px-1 py-1 text-xs">
                No values available
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchModeToggle({
  mode,
  onChange,
  disabled,
}: {
  mode: FacetMatchMode;
  onChange: (mode: FacetMatchMode) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`mb-1 flex items-center gap-1 px-1${disabled ? "pointer-events-none opacity-50" : ""}`}
    >
      <span className="text-base-content/50 text-xs">Match:</span>
      <div className="join join-horizontal">
        <button
          type="button"
          disabled={disabled}
          className={`btn join-item btn-xs ${mode === "or" ? "btn-primary" : "btn-ghost"}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange("or");
          }}
        >
          Any
        </button>
        <button
          type="button"
          disabled={disabled}
          className={`btn join-item btn-xs ${mode === "and" ? "btn-primary" : "btn-ghost"}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange("and");
          }}
        >
          All
        </button>
        <button
          type="button"
          disabled={disabled}
          className={`btn join-item btn-xs ${mode === "exact" ? "btn-primary" : "btn-ghost"}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange("exact");
          }}
        >
          Exact
        </button>
      </div>
    </div>
  );
}
